import { useEffect, useState } from 'react';
import { getAllInquiries } from '../../api/inquiryAPI';
import { Card, CardContent, Typography, Box, Grid, Skeleton } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { useNavigate } from 'react-router-dom';

const TotalInquiry = () => {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInquiries = async () => {
      try {
        const response = await getAllInquiries();
        const data = response?.data || response || [];
        const sortedData = Array.isArray(data)
          ? [...data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          : [];
        setInquiries(sortedData);
      } catch (error) {
        console.error('Error fetching inquiries:', error);
      } finally {
        setTimeout(() => setLoading(false), 1000);
      }
    };
    fetchInquiries();
  }, []);

  const renderCard = (label, count, icon, gradient) => (
    <Card
      className="fade-in"
      onClick={() => navigate('/inquiry')}
      sx={{
        width: '10vw',
        minWidth: 160,
        height: 120,
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        background: gradient,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
          pointerEvents: 'none',
        },
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
        },
      }}
    >
      {loading ? (
        <Skeleton variant="rounded" width="100%" height="100%" />
      ) : (
        <CardContent sx={{ p: 0, width: '100%', height: '100%', position: 'relative', zIndex: 1 }}>
          <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{
              mb: 1, p: 1.5, borderRadius: 2,
              background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            }}>
              {icon}
            </Box>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500, mb: 0.5 }}>
              {label}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#fff', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
              {count}
            </Typography>
          </Box>
        </CardContent>
      )}
    </Card>
  );

  // Simple breakdown: total, recent (last 7 days), older
  const recentCount = inquiries.filter(i => {
    const d = new Date(i.created_at);
    return (Date.now() - d.getTime()) < 7 * 24 * 60 * 60 * 1000;
  }).length;
  const olderCount = inquiries.length - recentCount;

  return (
    <>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: 'text.primary' }}>
        Inquiries Summary
      </Typography>
      <Grid container spacing={2}>
        <Grid item>
          {renderCard(
            'Total Inquiries',
            inquiries.length,
            <AssignmentIcon sx={{ fontSize: 32, color: '#fff' }} />,
            'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
          )}
        </Grid>
        <Grid item>
          {renderCard(
            'This Week',
            recentCount,
            <HourglassEmptyIcon sx={{ fontSize: 32, color: '#fff' }} />,
            'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
          )}
        </Grid>
        <Grid item>
          {renderCard(
            'Older',
            olderCount,
            <CheckCircleOutlineIcon sx={{ fontSize: 32, color: '#fff' }} />,
            'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default TotalInquiry;
